import isObject from 'lodash/isObject';
import each from 'lodash/each';
import keys from 'lodash/keys';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import isBoolean from 'lodash/isBoolean';

import type { Income, NameVariation } from '@jerry-serverless/types';

import type { ViolationType } from './violation';
import type { Education } from './education.enum';
import type { DriverRatedTypes } from './rated.enum';
import type { ExcludedReason } from './exclude_reason.enum';

export enum MaritalStatus {
  Single = 'Single',
  Married = 'Married',
  DomesticPartner = 'DomesticPartner',
  Widowed = 'Widowed',
  Separated = 'Separated',
  Divorced = 'Divorced',
}

export enum Gender {
  Male = 'Male',
  Female = 'Female',
}

export enum RelationToUser {
  Child = 'Child',
  DomesticPartner = 'DomesticPartner',
  Insured = 'Insured',
  Other = 'Other',
  Parent = 'Parent',
  Relative = 'Relative',
  Spouse = 'Spouse',
  Roommate = 'Roommate',
}

export const RelationToUserLabelMap: Record<RelationToUser, string> = {
  [RelationToUser.Spouse]: 'Spouse',
  [RelationToUser.Child]: 'Child',
  [RelationToUser.DomesticPartner]: 'Domestic Partner',
  [RelationToUser.Insured]: 'Insured',
  [RelationToUser.Other]: 'Other',
  [RelationToUser.Parent]: 'Parent',
  [RelationToUser.Relative]: 'Relative',
  [RelationToUser.Roommate]: 'Roommate',
};

export const DRIVER_FRAGMENT = `
  id
  isArchived
  name
  gender
  dateOfBirthMasked {
    value
  }
  age
  ageLicensed
  driverLicenseNumberMasked {
    value
  }
  driverLicenseState
  maritalStatus
  relationToUser
  occupation
  industry
  education
  driverTraining
  goodStudent
  violations {
    id
    dateReport
    description
    descriptionText
    points
    type
    mvrOrderID
  }
  rated
  excludedReason
  additionalInfo {
    genderSource
  }
`;

export interface Driver {
  id: string;
  isArchived?: boolean | null;
  name: string;
  gender?: Gender | null;
  dateOfBirthMasked?: { value: string } | null;
  age?: number | null;
  ageLicensed?: number | null;
  driverLicenseNumberMasked?: { value: string } | null;
  driverLicenseState?: string | null;
  driverLicenseStatus?: DriverLicenseStatusType | null;
  maritalStatus?: MaritalStatus | null;
  relationToUser?: RelationToUser | null;
  occupation?: string | null;
  industry?: string | null;
  education?: Education | null;
  driverTraining?: boolean | null;
  goodStudent?: boolean | null;
  violations?: ViolationType[];
  rated?: DriverRatedTypes | null;
  excludedReason?: ExcludedReason | null;
  additionalInfo?: (Partial<NameVariation> & { genderSource?: string }) | null;
}

export enum DriverLicenseStatusType {
  Cancelled = 'Cancelled',
  Expired = 'Expired',
  NotLicensed = 'NotLicensed',
  PermanentlyRevoked = 'PermanentlyRevoked',
  Permit = 'Permit',
  Suspended = 'Suspended',
  Valid = 'Valid',
}

export const DriverLicenseStatusOptions = [
  DriverLicenseStatusType.Valid,
  DriverLicenseStatusType.Suspended,
  DriverLicenseStatusType.Expired,
];

/**
 * Parse `Income` object from json object.
 *
 * @param obj
 */
export const parseIncome = (obj: Record<string, string>): Income => {
  const income: Income = {
    amount: null,
    averageHoursWeekly: null,
    frequency: null,
  };
  if (isObject(obj)) {
    each(keys(income), (key) => {
      if (isString(obj[key]) || isNumber(obj[key]) || isBoolean(obj[key])) {
        income[key] = obj[key];
      }
    });
  }
  return income;
};

export const MaritalStatusOptions = [
  {
    label: 'Married',
    value: MaritalStatus.Married,
  },
  {
    label: 'Single',
    value: MaritalStatus.Single,
  },
  {
    label: 'In a domestic partnership',
    value: MaritalStatus.DomesticPartner,
  },
];
